import React, { useState, useEffect, useCallback } from 'react';
import { getDatabase, ref, get, set, remove } from 'firebase/database';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, TrendingUp, FileText, Edit3, Eye, Trash2, Coins } from 'lucide-react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import './MaintenanceActive.css';
import { ComponentLifetime, TemplatesByType } from '../TemplatesEditor/types';
import { Building } from '../../../types/protocols';
import { BaseType } from '../../../types/Enums';
import { translate } from '../../../types/EnumTranslations';
import { SERVICE_COLORS } from '../../../Helpers/chartColors';
import { auth } from '../../../firebase';
import { useUsersData } from '../../../Helpers/UsersContext';
import { 
    generateYearlyPlansForBuilding, 
    YearlyActivity,
    ComponentMaintenanceData,
    MaintenancePlan,
    sortActivitiesByDate
} from '../../../Helpers/maintenanceCostUtils';
import MaintenanceActivityList from '../../../Components/MaintenanceActivityList';
import { Helmet } from 'react-helmet-async';

interface ComponentMaintenanceDataWithDone extends ComponentMaintenanceData {
    isDone?: boolean;
}

interface MaintenanceActivePlan extends MaintenancePlan {
    components: {
        [componentKey: string]: ComponentMaintenanceDataWithDone;
    };
}

interface MaintenanceProposal {
    components: {
        [componentKey: string]: ComponentMaintenanceData;
    };
    metadata: {
        savedAt: string;
        buildingKey: string;
    };
    submission: {
        status: 'pending' | 'approved' | 'rejected';
        submittedAt: string;
        submittedBy: string;
        reviewedBy?: string;
        reviewedAt?: string;
        rejectionComment?: string;
    };
}

const MaintenanceActive: React.FC = () => {
    const { buildingKey } = useParams<{ buildingKey: string }>();
    const navigate = useNavigate();
    const [user] = useAuthState(auth);
    const { users } = useUsersData();
    const [building, setBuilding] = useState<Building | null>(null);
    const [activePlan, setActivePlan] = useState<MaintenanceActivePlan | null>(null);
    const [componentLifetimes, setComponentLifetimes] = useState<Record<string, ComponentLifetime>>({});
    const [maintenanceTemplates, setMaintenanceTemplates] = useState<TemplatesByType>({});
    const [componentConditions, setComponentConditions] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [yearsToShow, setYearsToShow] = useState<number>(10);
    const [yearlyPlans, setYearlyPlans] = useState<YearlyActivity[]>([]);
    const [hasDraft, setHasDraft] = useState<boolean>(false);
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
    const [proposedPlan, setProposedPlan] = useState<MaintenanceProposal | null>(null);
    const [showArchiveModal, setShowArchiveModal] = useState<boolean>(false);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);

    const db = getDatabase();

    // Check if current user is admin
    useEffect(() => {
        if (user && users) {
            const currentUser = users.find(u => u.key === user.uid);
            setIsAdmin(currentUser?.admin || false);
        }
    }, [user, users]);

    // Cost Projection Chart Component
    const CostProjectionChart: React.FC<{ yearlyPlans: YearlyActivity[] }> = ({ yearlyPlans }) => {
        // Get all unique BaseTypes across all years
        const allBaseTypes = new Set<string>();
        yearlyPlans.forEach(plan => {
            Object.keys(plan.costsByBaseType).forEach(baseType => {
                allBaseTypes.add(baseType);
            });
        });
        const baseTypeKeys = Array.from(allBaseTypes).sort();

        const chartData = yearlyPlans.map(plan => {
            const data: any = {
                year: plan.year.toString(),
                totalCost: plan.totalCost
            };
            
            // Add each BaseType as a separate data key
            baseTypeKeys.forEach(baseType => {
                data[baseType] = plan.costsByBaseType[baseType] || 0;
            });
            
            return data;
        });

        return (
            <div className="cost-chart-section">
                <div className="cost-chart-card">
                    <div className="chart-header">
                        <h3 className="chart-title">
                            <TrendingUp size={20} className="me-2" />
                            Kostnadsprognos
                        </h3>
                        <p className="chart-subtitle">
                            Planerade kostnader per år för de kommande {yearsToShow} åren
                        </p>
                    </div>
                    <div className="chart-container">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={chartData}
                                margin={{ top: 20, right: 30, left: 30, bottom: 60 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="year"
                                    angle={-45}
                                    textAnchor="end"
                                    height={60}
                                    tickMargin={20}
                                    interval="preserveStartEnd"
                                />
                                <YAxis 
                                    label={{ value: 'Kostnad (kr)', angle: -90, position: 'insideLeft', offset: -15 }}
                                    tickFormatter={(value) => value.toLocaleString('sv-SE')}
                                />
                                <Tooltip 
                                    formatter={(value: number, name: string) => [
                                        `${value.toLocaleString('sv-SE')} kr`,
                                        translate(name as BaseType) || name
                                    ]}
                                    labelFormatter={(label) => `År ${label}`}
                                />
                                <Legend 
                                    formatter={(value) => translate(value as BaseType) || value}
                                />
                                {baseTypeKeys.map((baseType, index) => (
                                    <Bar
                                        key={baseType}
                                        dataKey={baseType}
                                        stackId="costs"
                                        fill={SERVICE_COLORS[index % SERVICE_COLORS.length]}
                                        name={baseType}
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        );
    };

    // Load data
    const loadData = useCallback(async (): Promise<void> => {
        try {
            setLoading(true);

            const [
                buildingSnapshot,
                summarySnapshot,
                activePlanSnapshot,
                lifetimesSnapshot,
                templatesSnapshot,
                conditionsSnapshot,
                draftSnapshot,
                proposedSnapshot
            ] = await Promise.all([
                get(ref(db, `buildings/${buildingKey}`)),
                get(ref(db, `buildingsSummary/${buildingKey}`)),
                get(ref(db, `maintenance/active/${buildingKey}`)),
                get(ref(db, 'maintenance/componentLifetimes')),
                get(ref(db, 'maintenance/templates')),
                get(ref(db, `componentConditions/${buildingKey}`)),
                get(ref(db, `maintenance/drafts/${buildingKey}`)),
                get(ref(db, `maintenance/proposed/${buildingKey}`))
            ]);

            // Load building with summary
            if (buildingSnapshot.exists()) {
                const buildingData = buildingSnapshot.val();
                const summaryData = summarySnapshot.exists() ? summarySnapshot.val() : { key: buildingKey };
                
                setBuilding({
                    key: buildingKey!,
                    model: buildingData,
                    summary: summaryData
                });
            }

            // Load active plan
            if (activePlanSnapshot.exists()) {
                const data = activePlanSnapshot.val() as MaintenanceActivePlan;
                setActivePlan(data);
            } else {
                setActivePlan(null);
            }

            // Load component lifetimes
            if (lifetimesSnapshot.exists()) {
                const data = lifetimesSnapshot.val() as Record<string, ComponentLifetime>;
                setComponentLifetimes(data);
            }

            // Load maintenance templates
            if (templatesSnapshot.exists()) {
                const data = templatesSnapshot.val() as Record<string, Record<string, any>>;
                const templatesByType: TemplatesByType = {};
                
                Object.entries(data).forEach(([componentType, componentTemplates]) => {
                    if (componentTemplates && typeof componentTemplates === 'object') {
                        templatesByType[componentType] = Object.entries(componentTemplates).map(([id, template]: [string, any]) => ({
                            id,
                            name: template.name || '',
                            description: template.description || '',
                            interval_type: template.interval_type || 'years',
                            interval_value: template.interval_value || 1,
                            cost: template.cost ?? 0,
                            cost_unit: template.cost_unit ?? 'per_sqm',
                            directionalIntervals: template.directionalIntervals || undefined
                        }));
                    }
                });
                setMaintenanceTemplates(templatesByType);
            }

            // Load component conditions
            if (conditionsSnapshot.exists()) {
                const data = conditionsSnapshot.val();
                setComponentConditions(data);
            }

            // Check for draft
            setHasDraft(draftSnapshot.exists());

            // Load proposed plan
            if (proposedSnapshot.exists()) {
                const data = proposedSnapshot.val() as MaintenanceProposal;
                setProposedPlan(data);
            }

        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    }, [buildingKey, db]);

    // Create new draft
    const handleCreateDraft = (): void => {
        setShowCreateModal(true);
    };

    const handleConfirmCreateDraft = async (): Promise<void> => {
        try {
            if (!building || !buildingKey) return;

            const newDraft = {
                components: {},
                metadata: {
                    savedAt: new Date().toISOString(),
                    buildingKey: buildingKey
                }
            };

            await set(ref(db, `maintenance/drafts/${buildingKey}`), newDraft);
            setShowCreateModal(false);
            navigate(`/maintenanceDrafts/${buildingKey}`);
        } catch (error) {
            console.error('Error creating draft:', error);
        }
    };

    const handleCancelCreateDraft = (): void => {
        setShowCreateModal(false);
    };

    const handleEditDraft = (): void => {
        navigate(`/maintenanceDrafts/${buildingKey}`);
    };

    const handleViewProposal = (): void => {
        navigate(`/maintenanceReview/${buildingKey}`);
    };

    // Archive plan handlers
    const handleArchivePlan = (): void => {
        setShowArchiveModal(true);
    };

    const handleConfirmArchive = async (): Promise<void> => {
        if (!activePlan || !buildingKey || !user) return;

        try {
            // Archive current active plan with readable timestamp
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const timestamp = `${year}-${month}-${day} ${hours}:${minutes}`; // Local time YYYY-MM-DD hh:mm format
            const archiveData = {
                ...activePlan,
                archivedAt: now.toISOString(),
                archivedBy: user.uid
            };
            await set(ref(db, `maintenance/archived/${buildingKey}/${timestamp}`), archiveData);
            
            // Remove active plan
            await remove(ref(db, `maintenance/active/${buildingKey}`));
            
            setShowArchiveModal(false);
            // Reload data to reflect changes
            await loadData();
        } catch (error) {
            console.error('Error archiving plan:', error);
        }
    };

    const handleCancelArchive = (): void => {
        setShowArchiveModal(false);
    };

    // Generate yearly plans using utility function
    const generateYearlyPlans = useCallback((): void => {
        if (!building || !activePlan || !componentLifetimes || !maintenanceTemplates) {
            setYearlyPlans([]);
            return;
        }

        const plans = generateYearlyPlansForBuilding({
            building,
            plan: activePlan,
            componentLifetimes,
            maintenanceTemplates,
            componentConditions,
            yearsToShow,
            buildingName: building.summary?.name
        });

        setYearlyPlans(plans);
    }, [building, activePlan, componentLifetimes, maintenanceTemplates, componentConditions, yearsToShow]);

    useEffect(() => {
        if (buildingKey) {
            loadData();
        }
    }, [buildingKey, loadData]);

    useEffect(() => {
        if (!loading) {
            generateYearlyPlans();
        }
    }, [loading, generateYearlyPlans]);

    if (loading) {
        return (
            <div className="loading-container">
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </div>
                    <p className="text-muted">Laddar underhållsplan...</p>
                </div>
            </div>
        );
    }

    if (!building) {
        return (
            <div className="alert alert-warning">
                <h4>Byggnad hittades inte</h4>
                <p>Den angivna byggnaden kunde inte hittas.</p>
            </div>
        );
    }

    if (!activePlan) {
        const buildingName = building?.summary?.name || 'Namnlös byggnad';
        
        return (
            <div className="maintenance-active">
                {/* Top Navigation Buttons */}
                <div className="top-navigation">
                    <div className="back-button-container">
                        <button
                            className="btn btn-outline-secondary"
                            onClick={() => navigate(`/building/${buildingKey}`)}
                        >
                            <ArrowLeft size={20} className="me-2" />
                            Tillbaka till byggnad
                        </button>
                    </div>
                    <div className="action-buttons-container">
                        {proposedPlan && proposedPlan.submission.status === 'pending' ? (
                            <button
                                className="btn btn-warning"
                                onClick={handleViewProposal}
                            >
                                <Eye size={20} className="me-2" />
                                Visa förslag
                            </button>
                        ) : hasDraft ? (
                            <button
                                className="btn btn-primary"
                                onClick={handleEditDraft}
                            >
                                <Edit3 size={20} className="me-2" />
                                Redigera utkast
                            </button>
                        ) : (
                            <button
                                className="btn btn-success"
                                onClick={handleCreateDraft}
                            >
                                <FileText size={20} className="me-2" />
                                Nytt utkast
                            </button>
                        )}
                    </div>
                </div>

                {/* Header */}
                <div className="header-section">
                    <div className="header-content">
                        <h1 className="display-6 fw-bold text-dark">
                            Underhållsplan - {buildingName}
                        </h1>
                    </div>
                </div>

                {/* Info Section */}
                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-md-10">
                            {proposedPlan && proposedPlan.submission.status === 'pending' ? (
                                <div className="alert alert-warning">
                                    <h4 className="alert-heading">Förslag på ny underhållsplan inväntar granskning</h4>
                                    <p className="mb-0">
                                        Ett förslag på en ny underhållsplan har lämnats in och väntar på granskning. 
                                        Klicka på "Visa förslag" för att granska det.
                                    </p>
                                </div>
                            ) : (
                                <div className="alert alert-info">
                                    <h4 className="alert-heading">Ingen aktiv underhållsplan</h4>
                                    <p className="mb-0">
                                        Det finns ingen aktiv underhållsplan för denna byggnad. 
                                        {hasDraft 
                                            ? ' Du har ett pågående utkast som du kan fortsätta arbeta med.'
                                            : ' Skapa ett nytt utkast för att komma igång med underhållsplaneringen.'
                                        }
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Create Draft Modal */}
                {showCreateModal && (
                    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Skapa nytt utkast</h5>
                                </div>
                                <div className="modal-body">
                                    <p>
                                        Är du säker på att du vill skapa ett nytt utkast för underhållsplan? 
                                        Detta kommer att skapa en ny plan baserat på byggnadens komponenter och befintliga mallar.
                                    </p>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={handleCancelCreateDraft}>
                                        Avbryt
                                    </button>
                                    <button type="button" className="btn btn-success" onClick={handleConfirmCreateDraft}>
                                        Skapa utkast
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    const totalActivities = yearlyPlans.reduce((total: number, year: YearlyActivity) => total + year.activities.length, 0);
    const totalCost = yearlyPlans.reduce((total: number, year: YearlyActivity) => total + year.totalCost, 0);
    const buildingName = building.summary?.name || 'Namnlös byggnad';

    return (
        <div className="maintenance-active" style={{padding: '40px'}}>
            <Helmet>
                <title>{`Underhållsplan - ${buildingName} - Molnfastigheter`}</title>
            </Helmet>
            {/* Top Navigation Buttons */}
            <div className="top-navigation">
                <div className="back-button-container">
                    <button
                        className="btn btn-outline-secondary"
                        onClick={() => navigate(`/building/${buildingKey}`)}
                    >
                        <ArrowLeft size={20} className="me-2" />
                        Tillbaka till byggnad
                    </button>
                </div>
                <div className="action-buttons-container">
                    {proposedPlan && proposedPlan.submission.status === 'pending' ? (
                        <button
                            className="btn btn-warning"
                            onClick={handleViewProposal}
                        >
                            <Eye size={20} className="me-2" />
                            Visa förslag
                        </button>
                    ) : hasDraft ? (
                        <button
                            className="btn btn-primary"
                            onClick={handleEditDraft}
                        >
                            <Edit3 size={20} className="me-2" />
                            Redigera utkast
                        </button>
                    ) : (
                        <button
                            className="btn btn-success"
                            onClick={handleCreateDraft}
                        >
                            <FileText size={20} className="me-2" />
                            Nytt utkast
                        </button>
                    )}
                    {isAdmin && activePlan && (
                        <button
                            className="btn btn-danger ms-2"
                            onClick={handleArchivePlan}
                        >
                            <Trash2 size={20} className="me-2" />
                            Arkivera underhållsplan
                        </button>
                    )}
                </div>
            </div>

            {/* Proposal Notification */}
            {proposedPlan && proposedPlan.submission.status === 'pending' && (
                <div className="proposal-notification-section">
                    <div className="alert alert-warning">
                        <h4 className="alert-heading">Förslag på ny underhållsplan inväntar granskning</h4>
                     <p className="mb-0">
                        Ett förslag på en ny underhållsplan har lämnats in och väntar på granskning. 
                        Den nuvarande planen visas nedan.
                    </p>
                </div>
            </div>
        )}

            {/* Header */}
            <div className="header-section">
                <div className="header-content" style={{marginTop: '30px', marginBottom: '30px'}} >
                    <h1 className="display-6 fw-bold text-dark">
                        Aktiv underhållsplan - {buildingName}
                    </h1>
                    <p className="lead text-muted">
                        Godkänd plan sparad {new Date(activePlan.metadata.savedAt).toLocaleDateString('sv-SE')}
                    </p>
                </div>
            </div>

            {/* Controls */}
            <div className="controls-section" style={{marginLeft: '16px', marginRight: '16px'}}>
                <div className="row align-items-center">
                    <div className="col-md-6">
                        <div className="d-flex align-items-center">
                            <label htmlFor="yearsToShow" className="form-label me-3 mb-0">
                                Visa prognos för:
                            </label>
                            <select 
                                id="yearsToShow"
                                className="form-select"
                                value={yearsToShow}
                                onChange={(e) => setYearsToShow(parseInt(e.target.value, 10))}
                                style={{ width: 'auto' }}
                            >
                                <option value={5}>5 år</option>
                                <option value={10}>10 år</option>
                                <option value={25}>25 år</option>
                                <option value={50}>50 år</option>
                            </select>
                        </div>
                    </div>
                    <div className="col-md-6 text-end">
                        <div className="stats-summary">
                            <span className="badge bg-success me-2">
                                <Calendar size={16} className="me-1" />
                                {totalActivities} aktiviteter
                            </span>
                            <span className="badge bg-warning">
                                <Coins size={16} className="me-1" />
                                {Math.round(totalCost).toLocaleString('sv-SE')} kr
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Cost Projection Chart */}
            {yearlyPlans.length > 0 && (
                <CostProjectionChart yearlyPlans={yearlyPlans} />
            )}

            {/* Yearly Plans */}
            <div className="yearly-plans">
                {yearlyPlans.map((yearPlan) => (
                    <div key={yearPlan.year} className="year-card">
                        <div className="year-header">
                            <h3 className="year-title">{yearPlan.year}</h3>
                            <div className="year-stats">
                                <span className="activity-count me-3">
                                    {yearPlan.activities.length} aktivitet{yearPlan.activities.length !== 1 ? 'er' : ''}
                                </span>
                                {yearPlan.totalCost > 0 && (
                                    <span className="year-cost">
                                        <Coins size={16} className="me-1" />
                                        {Math.round(yearPlan.totalCost).toLocaleString('sv-SE')} kr
                                    </span>
                                )}
                            </div>
                        </div>
                        
                        {yearPlan.activities.length > 0 ? (
                            <MaintenanceActivityList activities={sortActivitiesByDate(yearPlan.activities)} showCostCalculation={false} />
                        ) : (
                            <div className="no-activities">
                                Inga planerade aktiviteter detta år
                            </div>
                        )}
                    </div>
                ))}
            </div>

                {/* Create Draft Modal */}
                {showCreateModal && (
                    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Skapa nytt utkast</h5>
                                </div>
                                <div className="modal-body">
                                    <p>
                                        Är du säker på att du vill skapa ett nytt utkast för underhållsplan? 
                                        Detta kommer att skapa en ny plan baserat på byggnadens komponenter och befintliga mallar.
                                    </p>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={handleCancelCreateDraft}>
                                        Avbryt
                                    </button>
                                    <button type="button" className="btn btn-success" onClick={handleConfirmCreateDraft}>
                                        Skapa utkast
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Archive Plan Modal */}
                {showArchiveModal && (
                    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Arkivera underhållsplan</h5>
                                </div>
                                <div className="modal-body">
                                    <p>
                                        Detta arkiverar den aktiva underhållsplanen utan att ersätta med en ny. Vill du fortsätta?
                                    </p>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={handleCancelArchive}>
                                        Avbryt
                                    </button>
                                    <button type="button" className="btn btn-danger" onClick={handleConfirmArchive}>
                                        Arkivera
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

export default MaintenanceActive;